import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useCapability } from '../../../../hooks/use-capability';
import { usePresence } from '../../../../hooks/use-presence';
import { useAuthStore } from '../../../../stores/auth-store';
import { AgentNominalRow, BehavioralPatternCard, DerivasMatrix, PageHead } from '../../components';
import {
  useBehavioralContestations,
  useBehavioralPatterns,
  useQualityAgents,
  useQualityByOperator,
  useQualityByPlaybook,
  useQualityGlobal,
  useQualityPlaybookDrift,
  useQualityResolutionBySwitches,
  useQualitySequences,
  useQualityTopAttendants,
  useQualityTopBonds,
} from '../../hooks/use-quality';
import { bondLabelOf, SEQUENCE_FLAG_LABELS } from '../../labels';
import { FilterChip } from '../inbox-time/FilterChip';

import {
  adaptAgents,
  adaptBehavioralCards,
  adaptDerivas,
  adaptGlobals,
  adaptOperators,
  adaptPlaybooks,
  adaptSequences,
} from './tela6-adapter';

import type { SupervisorVariant } from '../../view-models';
import type React from 'react';

type QualityPeriod = 'hoje' | 'semana' | 'mes';

const PERIOD_OPTS: { value: QualityPeriod; label: string }[] = [
  { value: 'hoje', label: 'Hoje' },
  { value: 'semana', label: 'Semana' },
  { value: 'mes', label: 'Mês' },
];

function periodType(p: QualityPeriod): 'daily' | 'weekly' | 'monthly' {
  return p === 'hoje' ? 'daily' : p === 'semana' ? 'weekly' : 'monthly';
}

function periodDays(p: QualityPeriod): number {
  return p === 'hoje' ? 1 : p === 'semana' ? 7 : 30;
}

/** Janela [now-Nd, now] em datas ISO (yyyy-mm-dd) — D5. */
function periodWindow(p: QualityPeriod): { periodFrom: string; periodTo: string } {
  const to = new Date();
  const from = new Date(to.getTime() - periodDays(p) * 24 * 60 * 60 * 1000);
  return { periodFrom: from.toISOString().slice(0, 10), periodTo: to.toISOString().slice(0, 10) };
}

function mapTier(pkg: string | undefined): SupervisorVariant {
  if (pkg === 'solo') return 'solo';
  if (pkg === 'regulated') return 'regulated';
  return 'team-pro';
}

function SoloEmpty(): React.JSX.Element {
  return (
    <>
      <PageHead title="Dashboard de Qualidade" />
      <div style={{ padding: 'var(--space-10)', textAlign: 'center', color: 'var(--fg-3)' }}>
        <div style={{ fontSize: 48 }}>📊</div>
        <h3 style={{ color: 'var(--fg-1)' }}>Solo: dashboard simplificado</h3>
        <p>Apenas métricas globais + visão própria. Upgrade para Team para visão de equipe.</p>
      </div>
    </>
  );
}

function DistRow({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}): React.JSX.Element {
  return (
    <div className="quality-globals__dist-row">
      <span style={{ minWidth: 60, color: 'var(--fg-3)' }}>{label}</span>
      <div className="quality-globals__dist-bar">
        <div
          className="quality-globals__dist-bar-fill"
          style={{ width: `${value.toString()}%`, background: color }}
        />
      </div>
      <b style={{ color, minWidth: 30, textAlign: 'right' }}>{value}%</b>
    </div>
  );
}

export function DashboardQualidadePage(): React.JSX.Element {
  const variant = mapTier(useAuthStore((s) => s.tenant?.packageType));
  const canRead = useCapability('quality-aggregations-read');
  // Seção 6 (Postura) tem cap própria — oculta a seção se ausente (honesto).
  const canReadBehavioral = useCapability('behavioral-pattern-read');

  const [searchParams] = useSearchParams();

  const [period, setPeriod] = useState<QualityPeriod>('semana');
  // D6 — filtro Atendente client-side (só a tabela por atendente, Seção 2).
  // Item 352 — lazy initializer: seed inicial via `?attendantId=` da URL (salto do drawer
  // do atendente na Tela 1 Painel da Equipe). Só leitura no mount — não sincroniza
  // filtro↔URL nos dois sentidos (escopo de 1 fatia = 1 PR; mirror do Item 335).
  const [attendantFilter, setAttendantFilter] = useState<string | undefined>(
    () => searchParams.get('attendantId') ?? undefined,
  );
  const window = periodWindow(period);

  // Todos os hooks ANTES de qualquer early return (rules-of-hooks).
  const globalQ = useQualityGlobal(periodType(period));
  const operatorsQ = useQualityByOperator(window);
  const playbooksQ = useQualityByPlaybook(window);
  const agentsQ = useQualityAgents(window);
  const sequencesQ = useQualitySequences(window);
  const topAttendantsQ = useQualityTopAttendants(window);
  const topBondsQ = useQualityTopBonds(window);
  const resolutionBySwitchesQ = useQualityResolutionBySwitches(window);
  const driftQ = useQualityPlaybookDrift(window);
  const behavioralQ = useBehavioralPatterns();
  const contestationsQ = useBehavioralContestations();
  const { data: presence } = usePresence();

  if (variant === 'solo' || !canRead) return <SoloEmpty />;

  // Fallback de nome via presence (Engine já resolve no by-operator) — nunca id cru.
  const nameFor = (userId: string): string | undefined =>
    presence.find((p) => p.userId === userId)?.name ?? undefined;

  const globals = adaptGlobals(globalQ.data ?? []);
  const operators = adaptOperators(operatorsQ.data?.items ?? [], nameFor);
  // D6 — opções do chip Atendente = operadores presentes na tabela by-operator.
  const attendantOpts = operators.map((o) => ({ value: o.userId, label: o.name }));
  // Filtra SÓ a tabela por atendente (Seção 2); demais seções permanecem completas.
  const visibleOperators =
    attendantFilter !== undefined
      ? operators.filter((o) => o.userId === attendantFilter)
      : operators;
  const playbooks = adaptPlaybooks(playbooksQ.data ?? []);
  const { rows: agents, totals: agentTotals } = adaptAgents(agentsQ.data ?? []);
  const sequences = sequencesQ.data !== undefined ? adaptSequences(sequencesQ.data) : null;
  const derivas = adaptDerivas(driftQ.data ?? []);
  const behavioralCards = adaptBehavioralCards(behavioralQ.data?.items ?? [], nameFor);
  const pendingContestations = contestationsQ.data?.items ?? [];
  const hasDerivas = derivas.some((r) =>
    Object.values(r.to).some((v) => typeof v === 'number' && v > 0),
  );

  const isError = globalQ.isError || operatorsQ.isError || playbooksQ.isError || agentsQ.isError;
  const isLoading =
    globalQ.isLoading || operatorsQ.isLoading || playbooksQ.isLoading || agentsQ.isLoading;
  const isEmpty =
    !isLoading &&
    globals === null &&
    operators.length === 0 &&
    playbooks.length === 0 &&
    agents.length === 0;

  return (
    <>
      <PageHead
        title="Dashboard de Qualidade"
        subtitle={
          isEmpty || isError ? (
            'Visão estratégica · sentimento-primeiro (não dependente de CSAT)'
          ) : (
            <>
              Visão estratégica · sentimento-primeiro (não dependente de CSAT)
              {attendantFilter !== undefined && (
                <span
                  style={{ display: 'block', fontSize: 'var(--text-xs)', color: 'var(--fg-3)' }}
                >
                  O filtro de atendente afeta apenas a tabela por atendente.
                </span>
              )}
            </>
          )
        }
        filters={
          // A-44: o seletor de período precisa existir também no VAZIO — senão o supervisor fica
          // preso na janela sem dado, sem como trocar. Só o erro esconde os controles.
          isError ? undefined : (
            <div className="ti-filters">
              {PERIOD_OPTS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={'chip' + (period === opt.value ? ' chip--active' : '')}
                  onClick={() => {
                    setPeriod(opt.value);
                  }}
                >
                  {opt.label}
                </button>
              ))}
              {/* D6 — Atendente client-side; Equipe omitido (sem modelo de squad, Item 328). */}
              <FilterChip
                label="Atendente"
                options={attendantOpts}
                value={attendantFilter}
                onChange={setAttendantFilter}
              />
            </div>
          )
        }
      />

      {isError && (
        <div className="banner banner--danger" role="alert">
          <div className="banner__body">
            <span className="banner__title">Falha ao carregar o dashboard de qualidade.</span>
          </div>
        </div>
      )}

      {isEmpty && !isError && (
        <div style={{ padding: 'var(--space-10)', textAlign: 'center', color: 'var(--fg-3)' }}>
          <div style={{ fontSize: 48 }}>📊</div>
          {/* A-44 (noturna 04/Set/2026): «Sistema aprendendo padrões · ~7 dias» inventava uma
              causa. O vazio é da JANELA escolhida — o endpoint devolveu [] para a semana. */}
          <h3 style={{ color: 'var(--fg-1)' }}>Sem conversas encerradas neste período</h3>
          <p>
            As análises usam só conversas encerradas dentro da janela. Troque o período acima (Hoje
            · Semana · Mês).
          </p>
        </div>
      )}

      {!isEmpty && !isError && (
        <div className="tela6-body">
          {/* Seção 1 — Métricas globais (4 cards). Cada card degrada honesto. */}
          {globals !== null && (
            <section className="tela6-section">
              <h2>Métricas globais · sentimento-primeiro</h2>
              <div className="quality-globals">
                <div className="quality-globals__card">
                  <h3>Sentimento médio</h3>
                  <div className="quality-globals__value">
                    {globals.sentiment.value}
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-3)' }}>/10</span>
                  </div>
                  <div className="quality-globals__dist">
                    <DistRow
                      label="Positivo"
                      value={globals.sentiment.distribution.positive}
                      color="var(--success-fg)"
                    />
                    <DistRow
                      label="Neutro"
                      value={globals.sentiment.distribution.neutral}
                      color="var(--fg-3)"
                    />
                    <DistRow
                      label="Negativo"
                      value={globals.sentiment.distribution.negative}
                      color="var(--danger-fg)"
                    />
                  </div>
                </div>

                <div className="quality-globals__card">
                  <h3>Reabertura (proxy resolução)</h3>
                  <div className="quality-globals__value">
                    {globals.reopen.sevenDays}
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-3)' }}>% / 7d</span>
                  </div>
                  <div
                    className="quality-globals__delta"
                    style={{
                      color:
                        globals.reopen.status7 === 'warn'
                          ? 'var(--warning-fg)'
                          : 'var(--success-fg)',
                    }}
                  >
                    {globals.reopen.status7 === 'warn' ? '⚠' : '✓'} target &lt;
                    {globals.reopen.target7}%
                  </div>
                  <div className="tweaks-hint" style={{ marginTop: 6 }}>
                    30d: <b>{globals.reopen.thirtyDays}%</b>
                    {globals.reopen.status30 === 'warn' &&
                      ` ⚠ próximo do target ${globals.reopen.target30.toString()}%`}
                  </div>
                </div>

                <div className="quality-globals__card">
                  <h3>Tempo de resolução</h3>
                  {globals.resolutionTime.length > 0 ? (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                        fontSize: 'var(--text-xs)',
                        marginTop: 6,
                      }}
                    >
                      {globals.resolutionTime.map((r) => (
                        <div
                          key={r.label}
                          style={{ display: 'flex', justifyContent: 'space-between' }}
                        >
                          <span style={{ color: 'var(--fg-3)' }}>{r.label}</span>
                          <b style={{ color: 'var(--fg-1)' }}>{r.minutes}min</b>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="tweaks-hint" style={{ marginTop: 6 }}>
                      Sem dados de tempo de resolução no período.
                    </div>
                  )}
                </div>

                <div className="quality-globals__card">
                  <h3>Comportamento pós-atendimento</h3>
                  {globals.postBehavior.length > 0 ? (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                        fontSize: 'var(--text-xs)',
                        marginTop: 6,
                      }}
                    >
                      {globals.postBehavior.map((b) => (
                        <div
                          key={b.label}
                          style={{ display: 'flex', justifyContent: 'space-between' }}
                        >
                          <span style={{ color: 'var(--fg-3)' }}>{b.label}</span>
                          <b
                            style={{
                              color:
                                b.tone === 'good'
                                  ? 'var(--success-fg)'
                                  : b.tone === 'bad'
                                    ? 'var(--danger-fg)'
                                    : 'var(--fg-3)',
                            }}
                          >
                            {b.pct}%
                          </b>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="tweaks-hint" style={{ marginTop: 6 }}>
                      Sem dados de comportamento pós-atendimento no período.
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Seção 2 — Performance por atendente. */}
          {operators.length > 0 && (
            <section className="tela6-section">
              <h2>Performance por atendente</h2>
              <table className="q-table">
                <thead>
                  <tr>
                    <th>Atendente</th>
                    <th className="num">Conv</th>
                    <th className="num">Sent.</th>
                    <th className="num">SLA</th>
                    <th className="num">Sug. IA</th>
                    <th className="num">Pend. resol.</th>
                    <th className="num">Pend. venc.</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleOperators.map((a) => (
                    <tr key={a.userId} className={a.atRisk ? 'at-risk' : ''}>
                      <td>
                        <b>{a.name}</b>
                      </td>
                      <td className="num">{a.conv}</td>
                      <td className="num">
                        <b>{a.sent}</b>
                      </td>
                      <td className="num">{a.sla}%</td>
                      <td className="num">{a.ia}%</td>
                      <td className="num">{a.pendResolved}</td>
                      <td className="num">
                        {a.pendOverdue}
                        {a.atRisk && ' ⚠'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* Seção 3 — Performance por playbook. */}
          {playbooks.length > 0 && (
            <section className="tela6-section">
              <h2>Performance por playbook</h2>
              <table className="q-table">
                <thead>
                  <tr>
                    <th>Playbook</th>
                    <th className="num">Uso</th>
                    <th className="num">Conclusão</th>
                    <th className="num">Sent. pós</th>
                    <th className="num">Tempo médio</th>
                  </tr>
                </thead>
                <tbody>
                  {playbooks.map((p) => (
                    <tr key={p.playbookId} className={p.attention ? 'attention' : ''}>
                      <td>
                        <b>{p.name}</b>
                      </td>
                      <td className="num">{p.uso}</td>
                      <td className="num">
                        {p.conclusao}%{p.attention && ' ⚠'}
                      </td>
                      <td className="num">{p.sentPos}</td>
                      <td className="num">{p.tempoMin}min</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="tweaks-hint" style={{ marginTop: 'var(--space-2)' }}>
                ⚠ Playbooks abaixo de 85% conclusão merecem atenção.
              </div>
            </section>
          )}

          {/* Seção 4 — Performance por Agente Nominal (10 agentes ADR-036). */}
          {agents.length > 0 && (
            <section className="tela6-section">
              <h2>Performance por Agente IA Nominal</h2>
              <table className="q-table">
                <thead>
                  <tr>
                    <th>Agente</th>
                    <th className="num">Invocações</th>
                    <th className="num">Tokens</th>
                    <th className="num">Aceite</th>
                    <th className="num">Latência</th>
                  </tr>
                </thead>
                <tbody>
                  {agents.map((a) => (
                    <AgentNominalRow key={a.id} a={a} />
                  ))}
                  {/* Total client-side (D7) — Σ invocações. A Σ de custo saiu no
                      B-1 (17/Set/2026): era o custo da plataforma. */}
                  <tr
                    style={{
                      background: 'var(--bg-subtle)',
                      borderTop: '2px solid var(--border-strong)',
                    }}
                  >
                    <td>
                      <b>Total</b>
                    </td>
                    <td className="num">
                      <b>{agentTotals.invocacoes.toLocaleString('pt-BR')}</b>
                    </td>
                    <td className="num" />
                    <td className="num" />
                    <td className="num" />
                  </tr>
                </tbody>
              </table>
              <div className="tweaks-hint" style={{ marginTop: 'var(--space-2)' }}>
                * conductor: aceite = não precisou retomada (gatilho).
              </div>
            </section>
          )}

          {/* Seção 5 — Análise de Sequences. 4 counts + breakdowns reais por
              atendente/Bond (D1 — Item 328 resolvido, commit 30c4cc5a). */}
          {sequences !== null && (
            <section className="tela6-section">
              <h2>Análise de Sequences</h2>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  gap: 'var(--space-3)',
                }}
              >
                <div>
                  <h3 className="quality-sub-h">Flags · período</h3>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                      fontSize: 'var(--text-sm)',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>{SEQUENCE_FLAG_LABELS.highVolume}</span>
                      <b>{sequences.highVolume}</b>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>{SEQUENCE_FLAG_LABELS.escalating}</span>
                      <b style={{ color: 'var(--warning-fg)' }}>{sequences.escalating} ⚠</b>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>{SEQUENCE_FLAG_LABELS.allAbandoned}</span>
                      <b style={{ color: 'var(--danger-fg)' }}>{sequences.allAbandoned} ⚠</b>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>{SEQUENCE_FLAG_LABELS.crossChannel}</span>
                      <b>{sequences.crossChannel}</b>
                    </div>
                    <div
                      className="tweaks-hint"
                      style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}
                    >
                      <span>total</span>
                      <span>{sequences.total}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="quality-sub-h">Top atendentes · abandonadas</h3>
                  {topAttendantsQ.data === undefined ? (
                    <div className="tweaks-hint">Carregando…</div>
                  ) : topAttendantsQ.data.length === 0 ? (
                    <div className="tweaks-hint">Nenhuma sequência abandonada no período.</div>
                  ) : (
                    <ol className="quality-rank">
                      {topAttendantsQ.data.map((a) => (
                        <li
                          key={a.userId}
                          style={{ display: 'flex', justifyContent: 'space-between' }}
                        >
                          <span>{a.name}</span>
                          <b style={{ color: 'var(--danger-fg)' }}>{a.count}</b>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
                <div>
                  <h3 className="quality-sub-h">Top Bonds · sequences críticas</h3>
                  {topBondsQ.data === undefined ? (
                    <div className="tweaks-hint">Carregando…</div>
                  ) : topBondsQ.data.length === 0 ? (
                    <div className="tweaks-hint">Nenhuma sequência crítica no período.</div>
                  ) : (
                    <ol className="quality-rank">
                      {topBondsQ.data.map((b) => (
                        <li
                          key={b.bondId}
                          style={{ display: 'flex', justifyContent: 'space-between' }}
                        >
                          <span>{bondLabelOf(b.label)}</span>
                          <b style={{ color: 'var(--warning-fg)' }}>{b.count}</b>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Seção 6 — Análise de Postura Comportamental. Cap própria
              (behavioral-pattern-read): oculta a seção se ausente (honesto). */}
          {canReadBehavioral && behavioralCards.length > 0 && (
            <section className="tela6-section">
              <h2>Análise de Postura Comportamental</h2>
              <div className="beh-grid">
                {behavioralCards.map((p) => (
                  <BehavioralPatternCard key={p.id} pattern={p} />
                ))}
              </div>
              <div
                style={{
                  marginTop: 'var(--space-3)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <span className="tweaks-hint">
                  <b style={{ color: 'var(--warning-fg)' }}>{pendingContestations.length}</b>{' '}
                  contestações pendentes
                  {pendingContestations.length > 0 &&
                    ` · ${pendingContestations.map((c) => nameFor(c.operatorUserId) ?? '—').join(', ')}`}
                </span>
              </div>
            </section>
          )}

          {/* Seção 7 — Análise de Derivas (Eixo P). Matriz real pivotada +
              resolução por nº de trocas de playbook (D2 — Item 328 resolvido, 30c4cc5a). */}
          {hasDerivas && (
            <section className="tela6-section">
              <h2>Análise de Derivas conversacionais</h2>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 280px',
                  gap: 'var(--space-4)',
                }}
              >
                <DerivasMatrix transitions={derivas} />
                <div>
                  <h3 className="quality-sub-h">Resolução por nº de trocas de playbook</h3>
                  {resolutionBySwitchesQ.data === undefined ? (
                    <div className="tweaks-hint">Carregando…</div>
                  ) : resolutionBySwitchesQ.data.every((b) => b.total === 0) ? (
                    <div className="tweaks-hint">Sem conversas concluídas no período.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {resolutionBySwitchesQ.data.map((b) => (
                        <div
                          key={b.bucket}
                          style={{ display: 'flex', justifyContent: 'space-between' }}
                        >
                          <span>
                            {b.bucket === '0'
                              ? 'Sem troca'
                              : b.bucket === '1'
                                ? '1 troca'
                                : '2+ trocas'}
                          </span>
                          <b>
                            {b.total === 0 ? '—' : `${Math.round(b.resolutionRate * 100)}%`}
                            <span className="tweaks-hint"> ({b.total})</span>
                          </b>
                        </div>
                      ))}
                      <div className="tweaks-hint" style={{ marginTop: 4 }}>
                        % resolvidas entre as conversas concluídas (com desfecho) de cada faixa.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Seção 8 — Proatividade. GAP total de backend (D3, Item 328): nenhuma
              agregação de aceitação por categoria / taxa de override / fadiga
              comercial. Placeholder honesto único — NÃO renderizar sub-colunas
              com dado falso. */}
          <section className="tela6-section">
            <h2>Análise de Proatividade</h2>
            <div className="tweaks-hint">
              Em implementação — aceitação por categoria, taxa de override e fadiga comercial
              dependem de agregações ainda não disponíveis no backend.
            </div>
          </section>
        </div>
      )}
    </>
  );
}
